import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useBrand } from "../lib/brand";
import { PageHeader, Chip } from "../components/ui-bits";
import { Shield, LogIn, Building2 } from "lucide-react";

export default function SuperAdminPage() {
  const { user } = useAuth();
  const { refresh } = useBrand();
  const nav = useNavigate();
  const [orgs, setOrgs] = useState([]);
  const [brands, setBrands] = useState([]);

  useEffect(() => {
    if (!user?.is_super_admin) return;
    api.get("/admin/organizations").then(r=>setOrgs(r.data));
    api.get("/admin/brands").then(r=>setBrands(r.data));
  }, [user]);

  if (!user?.is_super_admin) return <div className="p-10 text-[#737373]">Super admin only.</div>;

  const toggleOrg = async (o) => {
    await api.patch(`/admin/organizations/${o.id}/status`, null, { params: { is_active: !o.is_active }});
    setOrgs(orgs.map(x=>x.id===o.id?{...x,is_active:!o.is_active}:x));
  };

  const enterBrand = async (b) => {
    await api.post(`/admin/impersonate/${b.id}`);
    await refresh();
    nav("/dashboard");
  };

  return (
    <div className="p-8 max-w-[1400px]" data-testid="super-admin-page">
      <PageHeader
        eyebrow="Platform admin"
        title="Super Admin"
        description="Cross-tenant view — every organization, every brand."
        action={<Chip tone="gold"><Shield size={12}/> &nbsp; PRIVILEGED</Chip>}
      />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <Kpi label="Organizations" value={orgs.length}/>
        <Kpi label="Brands" value={brands.length}/>
        <Kpi label="Active brands" value={brands.filter(b=>b.is_active).length}/>
        <Kpi label="Total users" value={orgs.reduce((s,o)=>s+(o.stats?.users||0),0)}/>
      </div>

      <div className="card-dark mb-8">
        <div className="p-4 border-b border-[#E5E5E5]">
          <div className="label-caps text-[#0A0A0A] flex items-center gap-2"><Building2 size={14} className="text-[#0A0A0A]"/> Organizations</div>
        </div>
        <table className="tbl">
          <thead><tr><th>Name</th><th>Owner</th><th>Users</th><th>Brands</th><th>Plan</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {orgs.map(o => (
              <tr key={o.id} data-testid={`org-row-${o.id}`}>
                <td>{o.name}</td>
                <td className="text-[#525252]">{o.billing_email || "—"}</td>
                <td>{o.stats?.users || 0}</td>
                <td>{o.stats?.brands || 0}</td>
                <td><Chip tone="default">{o.plan}</Chip></td>
                <td><Chip tone={o.is_active ? "green" : "red"}>{o.is_active ? "active" : "inactive"}</Chip></td>
                <td className="text-right">
                  <button onClick={()=>toggleOrg(o)} className="text-xs text-[#525252] hover:text-[#0A0A0A] uppercase tracking-widest">
                    {o.is_active ? "Deactivate" : "Activate"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="card-dark">
        <div className="p-4 border-b border-[#E5E5E5]">
          <div className="label-caps text-[#0A0A0A]">All Brands</div>
        </div>
        <table className="tbl">
          <thead><tr><th>Brand</th><th>Organization</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {brands.map(b => (
              <tr key={b.id} data-testid={`brand-row-${b.id}`}>
                <td>{b.name}</td>
                <td className="text-[#525252]">{b.organization_name || "—"}</td>
                <td><Chip tone={b.is_active ? "green" : "red"}>{b.is_active ? "active" : "inactive"}</Chip></td>
                <td className="text-right">
                  <button data-testid={`impersonate-${b.id}`} onClick={()=>enterBrand(b)} className="text-xs text-[#0A0A0A] hover:text-[#171717] uppercase tracking-widest inline-flex items-center gap-1">
                    <LogIn size={12}/> Enter
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Kpi({ label, value }) {
  return (
    <div className="card-dark p-6">
      <div className="label-caps mb-3">{label}</div>
      <div className="font-serif text-4xl">{value}</div>
    </div>
  );
}
